import { EnergySource, HvacArea } from '@belimo-retrofit-portal/logic';
import { FieldPath } from '@form-ts/core';
import { useFormWatch } from '@form-ts/react';
import React, { memo } from 'react';
import { FormattedHTMLMessage, useIntl } from 'react-intl';
import { FormattedMessage } from 'src/migration/react-intl';
import { Button } from 'src/modules/common/components/Button';
import { CarbonIntensityUnit } from 'src/modules/common/components/CarbonIntensityUnit';
import { EnergySourceUnit } from 'src/modules/common/components/EnergySourceUnit';
import { Tooltip } from 'src/modules/common/components/Tooltip';
import { FanIcon } from 'src/modules/common/icons/FanIcon';
import { InfoIcon } from 'src/modules/common/icons/InfoIcon';
import { LightningIcon } from 'src/modules/common/icons/LightningIcon';
import { ResetIcon } from 'src/modules/common/icons/ResetIcon';
import { DecimalField } from 'src/modules/form/components/DecimalField';
import { SwitchField } from 'src/modules/form/components/SwitchField';
import { FormError } from 'src/modules/form/types/FormError';
import { HvacFormAreaData, HvacFormData } from 'src/modules/hvac/types/HvacFormData';

type Props = {
  readonly resetSection: (area: HvacArea) => void;
  readonly path: FieldPath<HvacFormData, FormError, HvacFormAreaData>;
  readonly readOnly: boolean;
};

export const HvacSettingsVentilationCardView = memo(({ resetSection, path, readOnly }: Props) => {
  const intl = useIntl();
  const enabled = useFormWatch(path.form, path.at('enabled').value.get);

  return (
    <div className="bp-hvac-settings-view__card">
      <div className="bp-hvac-settings-view__card-header">
        <div className="bp-hvac-settings-view__card-icon">
          <FanIcon/>
        </div>

        <div className="bp-hvac-settings-view__card-title">
          <FormattedMessage id="hvac/tab/ventilation"/>
        </div>

        {!readOnly && (
          <div className="bp-hvac-settings-view__card-actions">
            <SwitchField
              field={path.at('enabled')}
            />

            <Tooltip id={`bp-hvac-settings-view-${path.path}`} value={intl.formatMessage({ id: 'hvac/energy/reset' })}>
              <Button
                kind="ghost"
                size="sm"
                iconOnly={true}
                onClick={() => resetSection(HvacArea.VENTILATION)}
                icon={(
                  <div className="bp-hvac-settings-view__card-icon">
                    <ResetIcon/>
                  </div>
                )}
              />
            </Tooltip>
          </div>
        )}
      </div>

      <div className="bp-hvac-settings-view__card-section">
        <div className="bp-hvac-settings-view__card-description">
          <div className="bp-hvac-settings-view__card-icon">
            <LightningIcon/>
          </div>

          <FormattedMessage id="hvac/energy/electricalEnergy/ventilation"/>
        </div>

        <DecimalField
          size="md"
          label={(
            <div className="bp-hvac-settings-view__card-energy-electrical-label">
              <FormattedMessage id="hvac/energy/annualEnergyUse"/>
            </div>
          )}
          field={path.at('electrical').at('annualEnergyUse')}
          disabled={!enabled}
          readOnly={readOnly}
          autoComplete="off"
          actions={<EnergySourceUnit source={EnergySource.ELECTRICITY}/>}
        />

        <DecimalField
          size="md"
          label={(
            <div className="bp-hvac-settings-view__card-energy-electrical-label">
              <FormattedMessage id="hvac/energy/carbonIntensity"/>

              <Tooltip
                place="top"
                id="bp-hvac-settings-ventilation-electrical-carbon-intensity"
                value={(
                  <div className="bp-hvac-settings-view__tooltip">
                    <FormattedHTMLMessage id="hvac/energy/carbonIntensity/tooltip"/>
                  </div>
                )}
              >
                <div
                  className="bp-hvac-settings-view__tooltip-icon"
                >
                  <InfoIcon/>
                </div>
              </Tooltip>
            </div>
          )}
          field={path.at('electrical').at('carbonIntensity')}
          disabled={!enabled}
          readOnly={readOnly}
          autoComplete="off"
          actions={<CarbonIntensityUnit source={EnergySource.ELECTRICITY}/>}
        />
      </div>
    </div>
  );
});
